import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useHistory } from "react-router-dom";
import routing from "routing";
import type { FaqContent } from "src/api/faq";
import { PrimaryButton, TextButton } from "src/components/Default/LekUI/Button";
import { useKeyPress } from "src/hooks/useKeyPress";

interface GuideNavigationProps {
	sections: FaqContent[];
	selectedId: number;
}

const getSectionPath = (section: FaqContent) =>
	generatePath(routing.paths.guide, { sectionId: section.id });

export const GuideNavigation = ({
	sections,
	selectedId,
}: GuideNavigationProps) => {
	const { t } = useTranslation();
	const history = useHistory();
	const index = sections.findIndex(({ id }) => id === selectedId);
	const previous = sections[index - 1];
	const next = sections[index + 1];

	useKeyPress(["ArrowLeft", "ArrowRight"], (event: KeyboardEvent) => {
		const target = event.key === "ArrowLeft" ? previous : next;

		if (target) {
			history.push(getSectionPath(target));
		}
	});

	if (!previous && !next) {
		return null;
	}

	return (
		<nav
			className="sticky-bottom sticky-bottom--transparent flex gap-12 pt-12 mt-auto"
			data-test-id="guide-navigation"
		>
			<TextButton
				{...(previous
					? { component: Link, to: getSectionPath(previous) }
					: { disabled: true })}
				className="btn-size-big btn-square bg-white border-medium-grey border-radius-12 text-black"
				aria-label={t("guide.navigation.previous", "Poprzednia sekcja")}
				data-test-id="guide-navigation-previous"
			>
				<ChevronLeft className="icon-small" />
			</TextButton>
			{next && (
				<PrimaryButton
					component={Link}
					to={getSectionPath(next)}
					className="btn-size-big border-radius-12 flex-1 min-w-0 justify-end size-16"
					endIcon={<ChevronRight className="icon-small" />}
					data-test-id="guide-navigation-next"
				>
					<span className="text-ellipsis-1">
						{index + 2}. {next.name}
					</span>
				</PrimaryButton>
			)}
		</nav>
	);
};
